import { useEffect, useState } from 'react';
import axios from 'axios';

import {
  PENDING_COMPANY_NAME_STORAGE_KEY,
  PENDING_TENANT_ID_STORAGE_KEY,
  readPendingCompanyName,
  readPendingTenantId,
  writePendingCompanyName,
} from '@/api/tenants/linkClerkOrg';
import { getBackendBaseUrl } from '@/api/backendAuth';
import { isPathBCustomOrgConfirmEnabled } from '@/utils/auth/pathBOrgConfirm';

const LOG = '[ChurnSignal][PathB-Org]';

/**
 * Path B company name: prefer localStorage, else load from the pre-created tenant row.
 * Only fetches from the API when custom org confirm needs a prefilled name — clerk mode
 * must not call /registration/.../company-name while signed in (Bearer + middleware used
 * to auto-provision a duplicate tenant before link-clerk-org).
 */
export function useResolvedPendingCompanyName(): {
  companyName: string | null;
  isResolving: boolean;
  hasPendingRegistration: boolean;
} {
  const pendingTenantId = readPendingTenantId();
  const [companyName, setCompanyName] = useState<string | null>(() => readPendingCompanyName());
  const [isResolving, setIsResolving] = useState(
    () =>
      isPathBCustomOrgConfirmEnabled() && Boolean(pendingTenantId) && !readPendingCompanyName(),
  );

  useEffect(() => {
    const storedName = readPendingCompanyName();
    const tenantId = readPendingTenantId();
    const customOrgConfirm = isPathBCustomOrgConfirmEnabled();

    console.info(LOG, 'resolvePendingCompanyName start', {
      tenantId,
      storedName,
      customOrgConfirm,
      localStorageTenantId: localStorage.getItem(PENDING_TENANT_ID_STORAGE_KEY),
      sessionStorageTenantId: sessionStorage.getItem(PENDING_TENANT_ID_STORAGE_KEY),
      localStorageCompanyName: localStorage.getItem(PENDING_COMPANY_NAME_STORAGE_KEY),
      sessionStorageCompanyName: sessionStorage.getItem(PENDING_COMPANY_NAME_STORAGE_KEY),
    });

    if (storedName) {
      setCompanyName(storedName);
      setIsResolving(false);
      console.info(LOG, 'using stored company name', { storedName });
      return;
    }

    // Default Clerk org UI does not need the registration company name — skip the API call.
    if (!customOrgConfirm) {
      setCompanyName(null);
      setIsResolving(false);
      console.info(LOG, 'skipping company-name API (Path B org confirm mode is clerk)');
      return;
    }

    if (!tenantId) {
      setCompanyName(null);
      setIsResolving(false);
      console.warn(LOG, 'no pending tenant id — Path B org confirm will NOT activate');
      return;
    }

    let cancelled = false;
    setIsResolving(true);
    const url = `${getBackendBaseUrl()}/registration/${tenantId}/company-name`;
    console.info(LOG, 'fetching company name from API', { url });

    (async () => {
      try {
        const { data, status } = await axios.get<{ companyName?: string }>(url);
        const name = data.companyName?.trim() || null;
        console.info(LOG, 'API company-name response', { status, data, name });
        if (!cancelled && name) {
          writePendingCompanyName(name);
          setCompanyName(name);
        } else if (!cancelled) {
          setCompanyName(null);
        }
      } catch (error: unknown) {
        console.warn(LOG, 'Failed to resolve pending company name from API', error);
        if (!cancelled) {
          setCompanyName(null);
        }
      } finally {
        if (!cancelled) {
          setIsResolving(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pendingTenantId]);

  const hasPendingRegistration = Boolean(pendingTenantId || companyName || readPendingCompanyName());

  useEffect(() => {
    console.info(LOG, 'resolvePendingCompanyName state', {
      pendingTenantId,
      companyName,
      isResolving,
      hasPendingRegistration,
    });
  }, [pendingTenantId, companyName, isResolving, hasPendingRegistration]);

  return {
    companyName,
    isResolving,
    hasPendingRegistration,
  };
}
